import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Phone, PhoneCall, Users, TrendingUp, Clock, Activity, PhoneOff, Briefcase, Calendar, DollarSign, Home } from 'lucide-react';

const TIME_FILTERS = [
  { label: '1h', value: '1h' },
  { label: '3h', value: '3h' },
  { label: '6h', value: '6h' },
  { label: 'Today', value: 'today' },
  { label: 'Week', value: 'week' },
];

const PIPELINE_COLORS = {
  new: '#3B82F6', contacted: '#06B6D4', interested: '#10B981',
  not_interested: '#EF4444', callback: '#F59E0B', not_picked: '#F97316',
  won: '#059669', lost: '#6B7280'
};

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = ['admin','toplevman','manager','marketing'].includes(user?.role);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [activityStats, setActivityStats] = useState(null);
  const [allCalls, setAllCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('today');
  const [agentFilter, setAgentFilter] = useState('');
  const [agents, setAgents] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [callsRes, actRes, configRes] = await Promise.all([
        api.get(`/api/calls?limit=200&time_filter=${timeFilter}${agentFilter ? '&agent_id='+agentFilter : ''}`),
        api.get('/api/activity/stats'),
        api.get('/api/config')
      ]);
      setAllCalls(callsRes.data.voip_calls || []);
      setActivityStats(actRes.data);
      setConfig(configRes.data);
      if (isAdmin) {
        const [adminRes, agentsRes] = await Promise.all([
          api.get('/api/admin/dashboard'),
          api.get('/api/agents')
        ]);
        setStats(adminRes.data);
        setAgents(agentsRes.data.agents || []);
      }
    } catch(err) { console.error(err); }
    finally { setLoading(false); }
  }, [timeFilter, agentFilter, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  const fmt = (s) => { if (!s) return '0s'; const m = Math.floor(s/60); return m > 0 ? `${m}m ${s%60}s` : `${s}s`; };

  const callsByAgent = allCalls.reduce((acc, c) => {
    const name = c.agent_name || 'Unknown';
    if (!acc[name]) acc[name] = { name, count: 0, duration: 0 };
    acc[name].count++;
    acc[name].duration += c.duration || 0;
    return acc;
  }, {});

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div></div>;

  return (
    <div className="space-y-6">
      <div className="glass rounded-xl p-6">
        <h2 className="text-2xl font-bold text-white mb-1">Welcome back, {user?.name}!</h2>
        <p className="text-dark-400">{config?.dashboard_headline || 'Cross-Border Real Estate CRM'}</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {isAdmin && stats ? (
          <>
            <StatCard icon={Users} label="VoIP Agents" value={stats.summary.voip_agents} color="green" />
            <StatCard icon={Users} label="Normal Agents" value={stats.summary.normal_agents} color="blue" />
            <StatCard icon={TrendingUp} label="Total Leads" value={stats.summary.total_leads} color="yellow" />
            <StatCard icon={Phone} label="VoIP Calls" value={stats.summary.total_voip_calls} color="purple" />
            <StatCard icon={Home} label="Properties" value={config?.total_properties_display || '0'} color="blue" />
            <StatCard icon={DollarSign} label="Sales" value={config?.total_sales_display || '0'} color="green" />
          </>
        ) : (
          <>
            <StatCard icon={Activity} label="Active Min" value={activityStats?.today_active_minutes || 0} color="green" />
            <StatCard icon={Clock} label="Idle Min" value={activityStats?.today_idle_minutes || 0} color="yellow" />
            <StatCard icon={PhoneCall} label="Calls Today" value={allCalls.length} color="blue" />
            <StatCard icon={TrendingUp} label="Logins" value={activityStats?.total_logins_today || 0} color="purple" />
            <StatCard icon={Calendar} label="Meetings" value="-" color="blue" />
            <StatCard icon={Briefcase} label="Deals" value="-" color="green" />
          </>
        )}
      </div>

      {/* Pipeline Summary */}
      {isAdmin && stats?.pipeline_summary && (
        <div className="glass rounded-xl p-5">
          <h3 className="text-lg font-semibold text-white mb-4">Pipeline Overview</h3>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
            {Object.entries(stats.pipeline_summary).map(([status, count]) => (
              <div key={status} className="text-center p-3 rounded-xl" style={{background: PIPELINE_COLORS[status]+'18', border: `1px solid ${PIPELINE_COLORS[status]}30`}}>
                <div className="text-xl font-bold text-white">{count}</div>
                <div className="text-xs mt-1 capitalize" style={{color: PIPELINE_COLORS[status]}}>{status.replace('_',' ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agent Performance */}
      {isAdmin && stats?.agent_stats?.length > 0 && (
        <div className="glass rounded-xl p-5">
          <h3 className="text-lg font-semibold text-white mb-4">Agent Performance</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="text-left py-2 px-3 text-dark-400 font-medium">Agent</th>
                  <th className="text-right py-2 px-3 text-dark-400 font-medium">Calls</th>
                  <th className="text-right py-2 px-3 text-dark-400 font-medium">Talk Time</th>
                  <th className="text-right py-2 px-3 text-dark-400 font-medium">Active</th>
                  <th className="text-right py-2 px-3 text-dark-400 font-medium">Leads</th>
                  <th className="text-right py-2 px-3 text-dark-400 font-medium">Commission %</th>
                </tr>
              </thead>
              <tbody>
                {stats.agent_stats.map(a => (
                  <tr key={a.id} className="border-b border-dark-800 hover:bg-dark-800/30">
                    <td className="py-2 px-3 text-white">{a.name}</td>
                    <td className="py-2 px-3 text-right text-white">{(a.voip_calls||0)+(a.manual_calls||0)}</td>
                    <td className="py-2 px-3 text-right text-white">{fmt(a.total_talk_time_seconds)}</td>
                    <td className="py-2 px-3 text-right text-white">{a.active_minutes_today}m</td>
                    <td className="py-2 px-3 text-right text-white">{a.assigned_leads}</td>
                    <td className="py-2 px-3 text-right text-primary-400">{a.commission_rate || 1}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Call Stats by Agent */}
      {isAdmin && Object.keys(callsByAgent).length > 0 && (
        <div className="glass rounded-xl p-5">
          <h3 className="text-lg font-semibold text-white mb-3">Calls by Agent <span className="text-sm text-dark-400 font-normal">({timeFilter})</span></h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.values(callsByAgent).sort((a,b) => b.count-a.count).map(a => (
              <div key={a.name} className="bg-dark-800/50 rounded-xl p-3">
                <p className="text-white font-medium text-sm">{a.name}</p>
                <p className="text-2xl font-bold text-primary-400">{a.count}</p>
                <p className="text-dark-400 text-xs">calls · {fmt(a.duration)} talk time</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Calls with Filters */}
      <div className="glass rounded-xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold text-white">VoIP Calls</h3>
          <div className="flex flex-wrap gap-2">
            {TIME_FILTERS.map(f => (
              <button key={f.value} onClick={() => setTimeFilter(f.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${timeFilter === f.value ? 'bg-primary-500 text-dark-900' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'}`}>
                {f.label}
              </button>
            ))}
            {isAdmin && (
              <select value={agentFilter} onChange={e => setAgentFilter(e.target.value)}
                className="px-3 py-1.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-xs">
                <option value="">All Agents</option>
                {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            )}
          </div>
        </div>
        <p className="text-dark-400 text-xs mb-3">{allCalls.length} calls found</p>
        {allCalls.length === 0 ? (
          <div className="text-center py-6 text-dark-400"><PhoneOff className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No calls in this period</p></div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {allCalls.slice(0,50).map(call => (
              <div key={call.id} className="flex items-center justify-between p-3 bg-dark-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${call.direction === 'outbound' ? 'bg-blue-500/20' : 'bg-green-500/20'}`}>
                    <PhoneCall className={`w-4 h-4 ${call.direction === 'outbound' ? 'text-blue-400' : 'text-green-400'}`} />
                  </div>
                  <div>
                    <p className="text-white text-sm">
                      {call.direction === 'inbound' ? call.from_number : call.to_number}
                    </p>
                    <p className="text-dark-400 text-xs">
                      {call.agent_name && <span className="text-primary-400 mr-1">{call.agent_name} ·</span>}
                      {call.direction} · {new Date(call.timestamp).toLocaleString()}
                      {call.direction === 'inbound' && call.routed_to && <span className="ml-1 text-dark-500">→ {call.routed_to.includes('all_executives') ? 'all executives' : call.routed_to}</span>}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-0.5 rounded text-xs ${call.status === 'completed' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>{call.status}</span>
                  <p className="text-dark-400 text-xs mt-1">{fmt(call.duration)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = { green: 'bg-green-500/10 text-green-400 border-green-500/20', blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20', yellow: 'bg-primary-500/10 text-primary-400 border-primary-500/20', purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20' };
  return (
    <div className="glass rounded-xl p-4">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center border mb-2 ${colors[color]}`}><Icon className="w-4 h-4" /></div>
      <div className="text-xl font-bold text-white">{value}</div>
      <div className="text-xs text-dark-400">{label}</div>
    </div>
  );
}
